<template>
    <aside class="chart-legend">
        <ul>
            <li
                v-for="(tagSeries, idx) in finalSeries"
                :key="tagSeries.id">
                <pendo-tag
                    :key="tagSeries.id"
                    class="chart-legend-capsule"
                    :class="{ 'chart-legend-capsule--HIDDEN': hiddenSeriesMap[tagSeries.id] }"
                    type="filter"
                    tabindex="0"
                    :label="tagSeries.name"
                    role="button"
                    :aria-pressed="!hiddenSeriesMap[tagSeries.id]"
                    :tooltip="showTooltip ? tagSeries.name : ''"
                    :prefix-icon="setLegendIcon(tagSeries)"
                    @click="toggleVisibility(tagSeries)"
                    @keydown.space="toggleVisibility(tagSeries)"
                    @keydown.enter="toggleVisibility(tagSeries)"
                    @focusin="mouseover(tagSeries)"
                    @focusout="mouseout(tagSeries)"
                    @mouseover="mouseover(tagSeries)"
                    @mouseout="mouseout(tagSeries)">
                    <div class="chart-legend-body">
                        <pendo-icon
                            v-if="tagSeries.sourceType === 'anyActivity'"
                            class="icon"
                            size="12"
                            :type="tagSeries.icon.type" />
                        <span
                            ref="chartLegendLabelText"
                            v-pendo-tooltip="{
                                content: showTooltip ? tagSeries.name : '',
                                arrow: true,
                                classes: 'chart-legend-label-text__tooltip',
                            }"
                            class="chart-legend-label-text"
                            @mouseover="checkOverflow(idx)">{{ tagSeries.name }}</span>
                    </div>
                </pendo-tag>
            </li>
        </ul>
    </aside>
</template>

<script>
import ChartColors from '../chart-colors.js';
import { PendoTag, PendoIcon, PendoTooltip } from '@pendo/components';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import isEqualWith from 'lodash/isEqualWith';
import cloneDeep from 'lodash/cloneDeep';

const ONLY_TOTAL_ACTIVE_THRESHOLD = 4;

function ignoreFunctions (a, b) {
    if (isFunction(a) && isFunction(b)) {
        return true;
    }
}

export default {
    name: 'ChartLegend',
    components: {
        PendoTag,
        PendoIcon,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        series: {
            type: Array,
            default: () => [],
        },
        renderedSeries: {
            type: Array,
            default: () => [],
        },
        chart: {
            type: Object,
            default: () => ({}),
        },
        highlightSeriesOnHover: {
            type: Boolean,
            default: true,
        },
        onlyTotalActiveByDefault: {
            type: Boolean,
            default: false,
        },
        isAggregate: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['toggleVisibility'],
    data () {
        return {
            colorArray: ChartColors,
            renderedColors: {},
            hiddenSeriesMap: {},
            showTooltip: false,
        };
    },
    computed: {
        finalSeries () {
            return this.series.reduce((acc, singleSeries) => {
                const series = cloneDeep(singleSeries);

                if (!this.hiddenSeriesMap[series.id] && (!series.visible || series.showInLegend === false)) {
                    return acc;
                }

                const matchingColor = this.renderedColors[series.id];
                if (matchingColor) {
                    series.color = matchingColor;
                }
                acc.push(series);

                return acc;
            }, []);
        },
    },
    watch: {
        series () {
            this.hiddenSeriesMap = {};
        },
        renderedSeries (newRenderedSeries, oldRenderedSeries) {
            if (newRenderedSeries === oldRenderedSeries) {
                return;
            }

            /*
             * In certain cases (such as pattern-fill colors), Highcharts re-creates its entire chart object several times,
             * even when the object's properties are mostly equal. Vue reacts to every one of these re-assignments. If we
             * write a computed property that depends directly on renderedSeries, that computed property will also run on
             * every re-assignment.
             *
             * Since our finalSeries computed property performs a relatively expensive array reduction, this watcher has
             * been written to pull out just the properties we care about (namely, the series IDs and colors) and only
             * trigger the reactive cascade when those values change.
             */
            const colorEntries = newRenderedSeries.map((fullSeries) => [fullSeries.userOptions.id, fullSeries.color]);
            const newRenderedColors = Object.fromEntries(colorEntries);
            if (!isEqualWith(this.renderedColors, newRenderedColors, ignoreFunctions)) {
                this.renderedColors = newRenderedColors;
            }

            if (this.onlyTotalActiveByDefault && newRenderedSeries.length > ONLY_TOTAL_ACTIVE_THRESHOLD) {
                this.showTotalOnly();
            }
        },
    },
    methods: {
        showTotalOnly () {
            this.series.filter(({ id }) => id !== 'total').forEach(this.toggleVisibility);
        },
        mouseover (series) {
            if (!this.highlightSeriesOnHover) {
                return;
            }
            const isIterative = !this.isAggregate || series.isIterative;

            if (isIterative) {
                this.renderedSeries.forEach((renderedSingleSeries) => {
                    const { id } = renderedSingleSeries.userOptions;
                    if (id === series.id) {
                        renderedSingleSeries.setState('hover');
                    } else {
                        renderedSingleSeries.setState('inactive-fade');
                    }
                });
            } else {
                this.renderedSeries[0]?.data.forEach((renderedSingleSeries) => {
                    const id = renderedSingleSeries.name;
                    if (id === series.name) {
                        renderedSingleSeries.setState('hover');
                    } else {
                        renderedSingleSeries.setState('inactive-fade');
                    }
                });
            }
        },
        mouseout (series) {
            if (!this.highlightSeriesOnHover) {
                return;
            }
            const isIterative = !this.isAggregate || series.isIterative;
            const renderedSeries = isIterative ? this.renderedSeries : this.renderedSeries[0].data;
            renderedSeries.forEach((renderedSingleSeries) => {
                renderedSingleSeries.setState('normal');
            });
        },
        setLegendIcon (series) {
            if (!series.marker) {
                series.marker = {};
            }
            const seriesColor = series.color?.pattern?.color || series.color;
            const stroke = series.marker.lineColor || seriesColor || this.colorArray[series.colorIndex];
            const strokeWidth = series.marker.strokeWidth || 4;
            const fill = series.marker.fillColor || seriesColor || this.colorArray[series.colorIndex];
            const type = series.dashStyle === 'Dot' ? 'more-horizontal' : series.marker.symbol || 'circle';

            return {
                type,
                stroke,
                fill,
                size: 12,
                strokeWidth,
            };
        },
        toggleVisibility (series) {
            // In the built-in legend that ships with Highcharts, the legendItemClick callback is used to
            // override default legend click behavior. Respect that same method here in Pendo-specific code.
            // https://api.highcharts.com/highcharts/plotOptions.series.events.legendItemClick
            if (isFunction(get(series, 'events.legendItemClick'))) {
                series.events.legendItemClick.call(series);

                return;
            }

            this.hiddenSeriesMap[series.id] = !this.hiddenSeriesMap[series.id];
            const isIterative = !this.isAggregate || series.isIterative;
            if (isIterative) {
                this.renderedSeries.forEach((renderedSingleSeries) => {
                    const { id } = renderedSingleSeries.userOptions;
                    if (id === series.id) {
                        renderedSingleSeries.setVisible(!this.hiddenSeriesMap[series.id], false);
                    }
                });
            } else {
                this.renderedSeries[0].data.forEach((renderedSingleSeries) => {
                    const id = renderedSingleSeries.name;
                    if (id === series.name) {
                        renderedSingleSeries.setVisible(!this.hiddenSeriesMap[series.id], false);
                    }
                });
                // For aggregate charts, we need to explicitly redraw after changing point visibility
                if (this.chart && this.chart.redraw) {
                    this.chart.redraw();
                }
            }

            this.hiddenSeriesMap = Object.assign({}, this.hiddenSeriesMap); // force refresh
            this.$emit('toggleVisibility');
        },
        checkOverflow (idx) {
            const { chartLegendLabelText } = this.$refs;
            this.showTooltip = chartLegendLabelText[idx]?.offsetWidth < chartLegendLabelText[idx]?.scrollWidth;
        },
    },
};
</script>

<style scoped lang="less">
.icon {
    padding-right: 4px;
}

.chart-legend-body {
    display: flex;
    align-items: center;

    .chart-legend-label-text {
        min-width: 0;
        max-width: 300px;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }
}

.chart-legend {
    display: flex;
    justify-content: center;
    flex-flow: wrap;
    margin: auto;

    ul {
        list-style: none;
        display: flex;
        justify-content: center;
        flex-flow: wrap;
        margin: auto;
        padding: 0;
        margin: 0;
    }

    &-capsule {
        position: relative;
        text-transform: none;
        cursor: pointer;
        margin: 8px 0 0 8px;
        transition: border-color 200ms;
        min-width: 0;

        // Grab the span that encloses pendo-tag's default slot
        // And remove the implicit "auto" min-width of that flex child
        // So that our ellipsis truncation takes place
        :deep(& > span) {
            min-width: 0;
        }

        &:hover {
            border-color: var(--palette-gray-100);
        }

        &--HIDDEN {
            opacity: 0.5;
        }
    }
}
</style>

<!-- tooltips are outside the scope of the blade  -->
<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="less">
.chart-legend-label-text__tooltip {
    .tooltip-inner {
        padding: 4px !important;
    }
}
</style>
